// Core
import { CSSProperties, ReactNode } from 'react';
// Packages
import { Row as AntRow } from 'antd';
import { Gutter } from 'antd/lib/grid/row';

interface IRowProps {
  align?: 'top' | 'middle' | 'bottom';
  dataCy?: string;
  gutter?: Gutter | [Gutter, Gutter];
  justify?: 'start' | 'end' | 'center' | 'space-around' | 'space-between';
  wrap?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/**
 * 24 Grids System.
 *
 * @param align - Vertical alignment
 * @param dataCy - prop for test purpose;
 * @param gutter - Spacing between grids, could be a number or a object like { xs: 8, sm: 16, md: 24}.
 * Or you can use array to make horizontal and vertical spacing work at the same time [horizontal, vertical]
 * @param justify -	Horizontal arrangement
 * @param wrap -	Auto wrap line
 * @param className - Component className
 * @param style - To customize the styles
 * @param children - Some ReactNode
 */

const Row = ({
  align = 'top',
  dataCy,
  gutter = 0,
  justify = 'start',
  wrap = true,
  className,
  style,
  children,
}: IRowProps) => {
  // On browsers with flex-gap support antd writes the vertical gutter straight
  // onto the element as an inline `row-gap` — including when that gutter is 0,
  // which is the default. An inline declaration outranks any class, so a
  // `row-gap` a page's *.module.scss sets on a control bar silently never
  // applies and the wrapped buttons sit flush against the filters on mobile.
  // Drop the inline 0 so the stylesheet owns the property; a real vertical
  // gutter is still left to antd.
  const verticalGutter = Array.isArray(gutter) ? gutter[1] : 0;
  const mergedStyle = verticalGutter ? style : { rowGap: undefined, ...style };

  return (
    <AntRow
      align={align}
      data-cy={dataCy}
      gutter={gutter}
      justify={justify}
      wrap={wrap}
      className={className}
      style={mergedStyle}
    >
      {children}
    </AntRow>
  );
};

export default Row;
